// ============================================================================
// embeddedWidgets — materialise the Accounts / Respondents widget sections
// ============================================================================
// A section authored as `{ type: 'accounts', accounts: {…} }` or
// `{ type: 'respondents', respondents: {…} }` expands into a marker section with
// `render_as` set and an EMPTY `fields[]`. Empty fields means the section is
// excluded from validation, value collection and Save — the concrete component
// (resolved via embeddedSectionRegistry) self-persists through its own REST
// endpoints. See ADR-0002 §2.4.

import type { SectionSpec, EmbeddedWidgetConfig } from 'api/types/formSpec.types';

export function expandAccountsSection(section: SectionSpec): SectionSpec {
  const cfg: EmbeddedWidgetConfig = section.accounts ?? {};
  return {
    key: section.key,
    title: cfg.title ?? section.title ?? 'Accounts',
    description: cfg.description ?? section.description,
    tab_label: section.tab_label,
    fields: [],
    render_as: 'accounts',
    accounts: cfg,
  };
}

export function expandRespondentsSection(section: SectionSpec): SectionSpec {
  const cfg: EmbeddedWidgetConfig = section.respondents ?? {};
  return {
    key: section.key,
    title: cfg.title ?? section.title ?? 'Respondents',
    description: cfg.description ?? section.description,
    tab_label: section.tab_label,
    fields: [],
    render_as: 'respondents',
    respondents: cfg,
  };
}

export function expandCaseDetailsSection(section: SectionSpec): SectionSpec {
  const cfg: EmbeddedWidgetConfig = section.case_details ?? {};
  return {
    key: section.key,
    title: cfg.title ?? section.title ?? 'Case Details',
    description: cfg.description ?? section.description,
    tab_label: section.tab_label,
    fields: [],
    render_as: 'case_details',
    case_details: cfg,
  };
}

export const isAccountsSection = (s: SectionSpec): boolean =>
  s.type === 'accounts' || s.render_as === 'accounts' || !!s.accounts;

export const isRespondentsSection = (s: SectionSpec): boolean =>
  s.type === 'respondents' || s.render_as === 'respondents' || !!s.respondents;

export const isCaseDetailsSection = (s: SectionSpec): boolean =>
  s.type === 'case_details' || s.render_as === 'case_details' || !!s.case_details;
